import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useNotifications } from "../hooks/use-notifications";
import { Skeleton } from "@/components/ui/skeleton";
import { format, parseISO, isSameDay, isSameMonth, isSameYear } from "date-fns";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IconBell, IconClock, IconExternalLink, IconSparkles } from '@tabler/icons-react';

interface NotificationsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDateRange = (startDate: string, endDate: string): string => {
  try {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    
    if (isSameDay(start, end)) {
      return format(start, "MMMM d, yyyy");
    }
    
    if (isSameYear(start, end) && isSameMonth(start, end)) {
      return `${format(start, "MMMM d")} - ${format(end, "d, yyyy")}`;
    }
    
    if (isSameYear(start, end)) {
      return `${format(start, "MMMM d")} - ${format(end, "MMMM d, yyyy")}`;
    }
    
    return `${format(start, "MMMM d, yyyy")} - ${format(end, "MMMM d, yyyy")}`;
  } catch {
    return `${startDate} - ${endDate}`;
  }
};

export const NotificationsModal = ({ open, onOpenChange }: NotificationsModalProps) => {
  const { data: notifications, isLoading, error } = useNotifications();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] p-0 flex flex-col">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <IconBell className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold text-foreground">Notifications</DialogTitle>
                <DialogDescription className="text-muted-foreground mt-1">
                  Stay updated with the latest news and updates
                </DialogDescription>
              </div>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6">
          <div className="py-6 space-y-4">
            {isLoading && (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="p-4 rounded-lg border border-border bg-[#f8f8f8] space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-2/3" />
                  </div>
                ))}
              </div>
            )}

            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-center">
                  <p className="text-destructive font-medium mb-1">Failed to load notifications</p>
                  <p className="text-sm text-muted-foreground">Please try again later</p>
                </div>
              </div>
            )}

            {!isLoading && !error && notifications?.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="p-6 rounded-lg bg-muted/50 border border-border text-center">
                  <IconSparkles className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-foreground font-medium mb-1">No notifications</p>
                  <p className="text-sm text-muted-foreground">Check back soon for updates</p>
                </div>
              </div>
            )}

            {!isLoading && !error && notifications && notifications.length > 0 && (
              <div className="space-y-3">
                {notifications.map((notification, index) => (
                  <div
                    key={`${notification.NotificationTitle}-${notification.NotificationStartDate}-${index}`}
                    className="p-5 rounded-lg border border-border bg-[#f8f8f8] hover:shadow-md transition-shadow"
                  >
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="p-1.5 rounded-full bg-primary/10 mt-0.5 flex-shrink-0">
                            <IconBell className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-foreground leading-tight mb-2">
                              {notification.NotificationTitle}
                            </h3>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                              <IconClock className="h-3 w-3 flex-shrink-0" />
                              <span>{formatDateRange(notification.NotificationStartDate, notification.NotificationEndDate)}</span>
                            </div>
                            {notification.NotificationDetails && (
                              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap mb-3">
                                {notification.NotificationDetails}
                              </p>
                            )}
                            {notification.NotificationHasURL && notification.NotificationURL && (
                              <Button
                                variant="default"
                                size="sm"
                                asChild
                                className="w-fit"
                              >
                                <a
                                  href={notification.NotificationURL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <IconExternalLink className="h-3 w-3 mr-2" />
                                  Learn More
                                </a>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

